import clsx from 'clsx';
import { Suspense, useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { Button } from '@/components/button/button';
import { DetailPageHeader } from '@/components/detailPageHeader/detailPageHeader';
import { Loader } from '@/components/loader/loader';
import { TabItem, Tabs } from '@/components/tabs/tabs';
import {
  useCloneMetricMutation,
  useCreateMetricVersionMutation,
  useDeprecateMetricVersionMutation,
  useMetricConfigQuery,
  useMetricQuery
} from '@/hooks/queries/useMetrics';
import {
  MetricProviderCreateReq,
  MetricProviderVersionCloneReq,
  MetricProviderVersionCreateReq
} from '@/types/evals';
import { getMetricIcon, getMetricType } from '@/utils/metricUtils';
import { parseParams } from '@/utils/searchParams';

import ModalMetricCreate from '../_modalMetricCreate/modalMetricCreate';
import { MetricDetailsTab } from './_metricDetailsTab/MetricDetailsTab';
import { MetricVersionConfigurationTab } from './_versionConfigurationTab/MetricVersionConfigurationTab';
import styles from './page.module.scss';

type SearchParams = {
  metric_provider_id: string;
  version: string;
  tab: string;
};

export type MetricTab = 'config' | 'details';

const METRIC_TAB_VALUES: readonly MetricTab[] = [ 'config', 'details' ] as const;

function isMetricTab( value: string | null | undefined ): value is MetricTab {
  return METRIC_TAB_VALUES.includes( value as MetricTab );
}

const METRIC_TABS: TabItem<MetricTab>[] = [
  { value: 'config', label: 'Version Configuration', 'data-testid': 'metric-tab-config' },
  { value: 'details', label: 'Metric Details', 'data-testid': 'metric-tab-details' }
];

function EvaluationMetricPage() {
  const [ searchParams, setSearchParams ] = useSearchParams();
  const {
    metric_provider_id: qMetricProviderId = '',
    version: qVersion = '',
    tab: qTab = ''
  } = parseParams<SearchParams>( searchParams );
  const navigate = useNavigate();

  const activeTab: MetricTab = isMetricTab( qTab ) ? qTab : 'config';
  const versionNumber = qVersion ? Number( qVersion ) : undefined;

  const metricQuery = useMetricQuery( qMetricProviderId );
  const configQuery = useMetricConfigQuery( qMetricProviderId, versionNumber );

  const cloneMutation = useCloneMetricMutation();
  const versionMutation = useCreateMetricVersionMutation();
  const deprecateMutation = useDeprecateMetricVersionMutation();

  const [ showModal, setShowModal ] = useState( false );
  const [ modalMode, setModalMode ] = useState<'clone' | 'version'>( 'clone' );

  const goToTab = useCallback( ( next: MetricTab ) => {
    setSearchParams( ( prev ) => {
      const params = new URLSearchParams( prev );
      params.set( 'tab', next );
      return params;
    } );
  }, [ setSearchParams ] );

  const updateVersion = useCallback( ( newVersion: number ) => {
    setSearchParams( ( prev ) => {
      const params = new URLSearchParams( prev );
      params.set( 'version', newVersion.toString() );
      return params;
    } );
  }, [ setSearchParams ] );

  const metric = metricQuery.data;
  const config = configQuery.data;

  // Next version number for the "Version N of …" modal title.
  const nextVersion = metric ? Math.max( 0, ...metric.versions.map( ( v ) => v.version ) ) + 1 : undefined;

  // Shape the current version's data for the create/clone modal. The backend
  // treats omitted fields as "inherit from source".
  const metricDataToEdit = config
    ? {
        type: getMetricType( config.configuration ),
        config: config.configuration,
        producesMetrics: config.produces_metrics,
        exampleType: config.example_type,
        name: metric?.name,
        description: metric?.description
      }
    : undefined;

  const openCloneModal = () => {
    setModalMode( 'clone' );
    setShowModal( true );
  };

  const openVersionModal = () => {
    setModalMode( 'version' );
    setShowModal( true );
  };

  const onClone = async( args: MetricProviderCreateReq ) => {
    if ( !config || !metric ) return;
    // Forward every editable field so user overrides take effect on the clone.
    const body: MetricProviderVersionCloneReq = {
      name: args.name,
      description: args.description,
      example_type: args.example_type,
      configuration: args.configuration,
      produces_metrics: args.produces_metrics
    };
    try {
      const resp = await cloneMutation.mutateAsync( { metricId: qMetricProviderId, version: qVersion, data: body } );
      toast( `Metric cloned successfully (${ resp.name })` );
      setShowModal( false );
      navigate( `/evaluations/metric?metric_provider_id=${ resp.id }&version=1` );
    } catch( e ) {
      console.error( e );
      toast( e instanceof Error ? e.message : 'Unable to clone metric' );
    }
  };

  const onCreateVersion = async( args: MetricProviderCreateReq ) => {
    if ( !config ) return;
    const body: MetricProviderVersionCreateReq = {
      example_type: args.example_type,
      configuration: args.configuration,
      produces_metrics: args.produces_metrics
    };
    try {
      const resp = await versionMutation.mutateAsync( { metricId: qMetricProviderId, data: body } );
      toast( `New metric version (${ resp.version }) created` );
      setShowModal( false );
      updateVersion( resp.version ?? 1 );
    } catch( e ) {
      console.error( e );
      toast( e instanceof Error ? e.message : 'Unable to create new metric version' );
    }
  };

  const onDeprecateToggle = async() => {
    if ( !config || versionNumber === undefined ) return;
    const next = !config.is_deprecated;
    try {
      await deprecateMutation.mutateAsync( {
        metricId: qMetricProviderId,
        version: versionNumber,
        data: { is_deprecated: next }
      } );
      toast( `metric ${ next ? 'deprecated' : 'undeprecated' }` );
    } catch( e ) {
      console.error( e );
      toast( 'Unable to update deprecation status' );
    }
  };

  if ( !qMetricProviderId ) {
    return (
      <div className='page'>
        <div>No metric specified</div>
      </div>
    );
  }

  // A failed fetch leaves isLoading false but metric/config undefined, so the
  // loading guard below would otherwise spin forever. Surface the error and
  // let the user retry instead.
  if ( metricQuery.isError || configQuery.isError ) {
    return (
      <div className='page'>
        <div className={ clsx( 'layout__col', 'pad' ) } data-testid="metric-load-error">
          <div>Unable to load metric.</div>
          <Button
            variant="secondary"
            size="sm"
            onClick={ () => {
              metricQuery.refetch();
              configQuery.refetch();
            } }
            data-testid="metric-load-retry"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if ( metricQuery.isLoading || configQuery.isLoading || !metric || !config ) {
    return (
      <div className='page'>
        <Loader />
      </div>
    );
  }

  return (
    <div className='page'>
      { showModal && (
        <ModalMetricCreate
          mode={ modalMode }
          cb={ modalMode === 'clone' ? onClone : onCreateVersion }
          onClose={ () => setShowModal( false ) }
          initial={ metricDataToEdit }
          nextVersion={ nextVersion }
          isSubmitting={ modalMode === 'clone' ? cloneMutation.isPending : versionMutation.isPending }
        />
      ) }

      <DetailPageHeader
        backTo="/evaluations/metrics"
        backLabel="Back to Metrics"
        icon={ getMetricIcon() }
        title={ config.name }
        subtitle={ `Metric (${ getMetricType( config.configuration ) })` }
        actions={
          (
            <Button variant="secondary" onClick={ openCloneModal } data-testid="clone-button">
              Clone Metric
            </Button>
          )
        }
      />

      <Tabs
        items={ METRIC_TABS }
        active={ activeTab }
        onChange={ goToTab }
        variant="plain"
        data-testid="metric-tabs"
      />

      <div className={ clsx( 'layout__col', styles.tabContent ) }>
        { activeTab === 'config' && (
          <MetricVersionConfigurationTab
            metric={ metric }
            config={ config }
            onNewVersion={ openVersionModal }
            onVersionChange={ updateVersion }
            onDeprecateToggle={ onDeprecateToggle }
            isDeprecating={ deprecateMutation.isPending }
            isCreatingVersion={ versionMutation.isPending }
          />
        ) }

        { activeTab === 'details' && (
          <MetricDetailsTab metric={ metric } config={ config } />
        ) }
      </div>
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={ <Loader /> }>
      <ToastContainer />
      <EvaluationMetricPage />
    </Suspense>
  );
}
